import { FeatureController, type BaseFeatureState, type PlayerAccessRights } from '@playcastdotio/FeatureController';
import { InputCaptureManager } from '../_base/InputCaptureManager';
import { BeaconAnalyticsManager } from './managers/BeaconAnalyticsManager';
import { connectBeaconFastStart, disconnect } from '../../api/modules/realtimeMessaging';
import { handleEvent } from '../../api/modules/hostMessaging/inputEvents';
import { beaconResolveState } from '../../api/stores/beaconStores';
import { playerConnectionState } from '../../api/stores/playerStores';
import { getPreFetchedIceServers } from '../../api/modules/iceServerPrefetch';
import { createPeer, closePeer, setPeerMilestoneCallbacks } from '../../api/modules/peer';
import { getPersistentPlayerId } from '../../api';
import { authStore } from '../../api/stores/globalStores';

export type BeaconJoinPhase =
  | 'page_viewed'
  | 'fast_starting'
  | 'connecting_peer'
  | 'playing'
  | 'kicked'
  | 'disconnected'
  | 'not_found'
  | 'error';

export interface BeaconJoinState extends BaseFeatureState {
  phase: BeaconJoinPhase;
  shareCode: string;
  sessionId: string;
  hostPeerId?: string;
  peerId?: string;
  streamTitle?: string;
  isReconnecting: boolean;
  connectionRetries: number;
  kickReason?: string;
  access?: PlayerAccessRights;
}

const MAX_RECONNECT_ATTEMPTS = 2;

export class BeaconJoinCoordinator extends FeatureController<BeaconJoinState> {
  private analyticsManager: BeaconAnalyticsManager;
  private inputCaptureManager: InputCaptureManager | null = null;
  private fastStartTimestamp = 0;
  private connectionStartTime = 0;
  private resolveListenerId?: string;
  private connectionListenerId?: string;
  private connectionTimeoutId?: NodeJS.Timeout;

  constructor(shareCode: string, debugPhase?: BeaconJoinPhase) {
    const sessionId = crypto.randomUUID?.() ?? Math.random().toString(36).slice(2);
    const initialPhase = debugPhase ?? 'page_viewed';

    super({
      status: 'idle',
      phase: initialPhase,
      shareCode,
      sessionId,
      isReconnecting: false,
      connectionRetries: 0,
      access: { mouse: true, keyboard: true, touch: true, gamepad: true },
    });

    this.analyticsManager = new BeaconAnalyticsManager({
      getState: () => this.state.currentState,
    });
  }

  protected async onInit(): Promise<void> {
    this.analyticsManager.sendPageViewed();
    this.startFastStart();
  }

  public transitionTo(phase: BeaconJoinPhase): void {
    if (this.isDestroyed) return;
    const previous = this.state.currentState.phase;
    if (this.isTerminalPhase(previous) && phase !== previous) {
      console.warn(`[BeaconJoinCoordinator] Blocked transition from terminal phase: ${previous} -> ${phase}`);
      return;
    }
    console.log(`[BeaconJoinCoordinator] ${previous} -> ${phase}`);
    this.state.updateState({ ...this.state.currentState, phase });
  }

  private startFastStart(): void {
    this.transitionTo('fast_starting');
    this.fastStartTimestamp = Date.now();
    this.analyticsManager.sendFastStartInitiated();

    this.resolveListenerId = beaconResolveState.addListener((resolveState) => {
      if (this.state.currentState.phase !== 'fast_starting') return;
      if (resolveState.status === 'resolved') {
        this.onFastStartSuccess(resolveState);
      } else if (resolveState.status === 'error') {
        this.onFastStartError(resolveState);
      }
    });
    this.addCleanup(() => {
      if (this.resolveListenerId) {
        beaconResolveState.removeListener(this.resolveListenerId);
      }
    });

    const currentResolve = beaconResolveState.currentState;
    if (currentResolve.status === 'resolved') {
      this.onFastStartSuccess(currentResolve);
      return;
    }
    if (currentResolve.status === 'error') {
      this.onFastStartError(currentResolve);
      return;
    }

    const persistentId = getPersistentPlayerId();
    const tokenJwt = authStore.currentState.accessToken || undefined;

    connectBeaconFastStart({
      shareCode: this.state.currentState.shareCode,
      persistentId,
      tokenJwt,
      requestIceServers: true,
    });
  }

  private async onFastStartSuccess(resolveState: typeof beaconResolveState.currentState): Promise<void> {
    const durationMs = Date.now() - this.fastStartTimestamp;
    this.analyticsManager.sendFastStartSucceeded(durationMs);

    const { hostPeerId, peerId, peerToken, iceServers, streamTitle } = resolveState;

    this.state.updateState({
      ...this.state.currentState,
      hostPeerId,
      peerId,
      streamTitle,
    });

    let finalIceServers = iceServers;
    if (!finalIceServers) {
      const prefetched = await getPreFetchedIceServers();
      if (prefetched) {
        finalIceServers = prefetched;
      }
    }

    playerConnectionState.updateState({
      ...playerConnectionState.currentState,
      playerPeerId: peerId!,
      peerToken,
      broadcastSessionId: hostPeerId!,
      iceServers: finalIceServers,
      status: 'idle',
      allowPeerConnections: true,
    });

    this.connectToPeer(hostPeerId!);
  }

  private onFastStartError(resolveState: typeof beaconResolveState.currentState): void {
    const durationMs = Date.now() - this.fastStartTimestamp;
    this.analyticsManager.sendFastStartFailed(
      resolveState.errorCode ?? 'UNKNOWN',
      resolveState.errorMessage ?? 'Unknown error',
      resolveState.errorStage ?? 'resolve',
    );

    if (resolveState.errorCode === 'NOT_FOUND') {
      this.analyticsManager.sendNotFound();
      this.transitionTo('not_found');
    } else {
      this.transitionTo('error');
    }
  }

  private connectToPeer(hostPeerId: string): void {
    this.transitionTo('connecting_peer');
    this.connectionStartTime = Date.now();
    this.analyticsManager.sendBroadcastJoinAttempted();

    setPeerMilestoneCallbacks({
      onPeerIdAcquired: (id) => this.analyticsManager.sendPeerIdAcquired(id),
      onSdpOfferSent: () => this.analyticsManager.sendSdpOfferSent(),
      onSdpAnswerReceived: () => this.analyticsManager.sendSdpAnswerReceived(),
      onIceCandidatesSent: (count) => this.analyticsManager.sendIceCandidatesSent(count),
      onIceChecking: () => this.analyticsManager.sendIceCheckingStarted(),
      onIceConnected: () => this.analyticsManager.sendIceConnected(),
      onRemoteStreamReceived: () => this.analyticsManager.sendRemoteStreamReceived(),
    });

    this.connectionListenerId = playerConnectionState.addListener((connState) => {
      const currentPhase = this.state.currentState.phase;
      if (this.isTerminalPhase(currentPhase)) return;

      const wasPlaying = currentPhase === 'playing';

      if (connState.status === 'connected') {
        this.onPeerConnected();
      } else if (connState.status === 'refused') {
        this.onPlayerKicked(connState.kickReason || 'Refused by host');
      } else if (connState.status === 'failed') {
        this.onPeerConnectionFailed(connState.kickReason || 'Connection failed');
      } else if (connState.status === 'disconnected' && wasPlaying) {
        if (this.isKickReason(connState.kickReason)) {
          this.onPlayerKicked(connState.kickReason!);
        } else {
          this.onPeerDisconnectedDuringPlay(connState.kickReason);
        }
      } else if (connState.status === 'disconnected' && !wasPlaying) {
        this.onPeerConnectionFailed(connState.kickReason || 'Disconnected before playing');
      }
    });
    this.addCleanup(() => {
      if (this.connectionListenerId) {
        playerConnectionState.removeListener(this.connectionListenerId);
      }
    });

    this.connectionTimeoutId = this.createTimeout(() => {
      this.onPeerConnectionFailed('Connection timeout');
    }, 30000);

    createPeer(hostPeerId);
  }

  private clearConnectionTimeout(): void {
    if (this.connectionTimeoutId) {
      clearTimeout(this.connectionTimeoutId);
      this.connectionTimeoutId = undefined;
    }
  }

  private onPeerConnected(): void {
    this.clearConnectionTimeout();
    const duration = Date.now() - this.connectionStartTime;
    this.analyticsManager.sendBroadcastJoinSucceeded(duration);
    this.analyticsManager.sendSessionStarted();
    this.transitionTo('playing');

    this.startInputCapture();

    this.addEventListenerSafe(document, 'visibilitychange', () => {
      this.analyticsManager.sendTabVisibilityChanged(!document.hidden);
    });
  }

  private startInputCapture(): void {
    this.inputCaptureManager?.cleanup();

    this.inputCaptureManager = new InputCaptureManager({
      access: this.state.currentState.access ?? { mouse: true, keyboard: true, touch: true, gamepad: true },
      onKeyboard: (event) => handleEvent(event),
      onMouse: (event) => handleEvent(event),
      onTouch: (event) => handleEvent(event),
      onGamepad: (gamepads) => {
        const event = new CustomEvent('gamepad', { detail: gamepads });
        handleEvent(event);
      },
    });
    this.inputCaptureManager.start();
  }

  private isTerminalPhase(phase: BeaconJoinPhase): boolean {
    return phase === 'kicked' || phase === 'disconnected' || phase === 'error' || phase === 'not_found';
  }

  private isKickReason(reason?: string): boolean {
    if (!reason) return false;
    const lower = reason.toLowerCase();
    return lower.includes('kick') || lower.includes('refused') || lower.includes('host:close');
  }

  private isHostTerminalReason(reason?: string): boolean {
    if (!reason) return false;
    const lower = reason.toLowerCase();
    return (
      lower.includes('host:stream_stopped') ||
      lower.includes('host:shutdown') ||
      lower.includes('host:close') ||
      lower.includes('host:kick')
    );
  }

  private onPlayerKicked(reason: string): void {
    this.clearConnectionTimeout();
    this.analyticsManager.sendPlayerDisconnected(reason);
    this.state.updateState({
      ...this.state.currentState,
      kickReason: reason,
    });
    this.transitionTo('kicked');
  }

  private onPeerConnectionFailed(reason: string): void {
    this.clearConnectionTimeout();
    const duration = Date.now() - this.connectionStartTime;
    this.analyticsManager.sendBroadcastJoinFailed(reason, duration);
    this.transitionTo('error');
  }

  private onPeerDisconnectedDuringPlay(kickReason?: string): void {
    this.clearConnectionTimeout();
    this.analyticsManager.sendPlayerDisconnected(kickReason);

    if (this.isHostTerminalReason(kickReason)) {
      this.state.updateState({ ...this.state.currentState, isReconnecting: false });
      this.transitionTo('disconnected');
      return;
    }

    const retries = this.state.currentState.connectionRetries;
    if (retries < MAX_RECONNECT_ATTEMPTS) {
      this.state.updateState({
        ...this.state.currentState,
        isReconnecting: true,
        connectionRetries: retries + 1,
      });
      this.analyticsManager.sendPlayerReconnecting(retries + 1);
      this.transitionTo('connecting_peer');
      this.connectionStartTime = Date.now();

      const hostPeerId = this.state.currentState.hostPeerId;
      if (hostPeerId) {
        createPeer(hostPeerId);
      } else {
        this.transitionTo('disconnected');
      }
    } else {
      this.state.updateState({
        ...this.state.currentState,
        isReconnecting: false,
      });
      this.transitionTo('disconnected');
    }
  }

  public disconnectPeer(reason: string): void {
    closePeer(reason);
  }

  public cleanup(): void {
    this.analyticsManager.sendSessionEnded('cleanup');
    this.inputCaptureManager?.cleanup();
    this.inputCaptureManager = null;
    this.disconnectPeer('beacon:cleanup');
    setPeerMilestoneCallbacks({});
    disconnect();
    this.analyticsManager.cleanup();
    super.cleanup();
  }
}
